/**
 * Polish Translations — Impact Core i18n
 *
 * Flat key-value structure. Keys use dot notation for namespacing.
 * Coverage: navigation, actions, statuses, dashboard labels, form labels.
 */

export const pl: Record<string, string> = {
  // Navigation
  'nav.dashboard': 'Panel',
  'nav.funds': 'Fundusze',
  'nav.investments': 'Inwestycje',
  'nav.investees': 'Podmioty inwestycji',
  'nav.esg': 'ESG Przegląd',
  'nav.compliance': 'Zgodność',
  'nav.reports': 'Raporty',
  'nav.settings': 'Ustawienia',
  'nav.users': 'Użytkownicy',
  'nav.sfdr': 'SFDR',
  'nav.pai': 'PAI Wskaźniki',
  'nav.taxonomy': 'Taksonomia UE',
  'nav.filings': 'Zgłoszenia',
  'nav.calendar': 'Kalendarz',
  'nav.submit': 'Prześlij dane',
  'nav.submissions': 'Moje zgłoszenia',
  'nav.benchmarks': 'Benchmarki',
  'nav.messages': 'Wiadomości',
  'nav.portfolio': 'Portfel',
  'nav.performance': 'Wyniki funduszy',
  'nav.esgSummary': 'ESG Podsumowanie',
  'nav.pipeline': 'Pipeline',
  'nav.dueDiligence': 'Badanie due diligence',
  'nav.impact': 'Wskaźniki wpływu',
  'nav.sdg': 'Mapowanie CZR',
  'nav.audit': 'Ślad audytu',
  'nav.snapshots': 'Migawki danych',
  'nav.knowledgeGraph': 'Graf wiedzy',
  'nav.geographic': 'Geograficzny',
  'nav.bi': 'BI Studio',
  'nav.ai': 'Asystent AI',
  'nav.tasks': 'Zadania',
  'nav.csrd': 'CSRD',
  'nav.regulatory': 'Monitor regulacyjny',

  // Common Actions
  'action.create': 'Utwórz',
  'action.edit': 'Edytuj',
  'action.delete': 'Usuń',
  'action.save': 'Zapisz',
  'action.cancel': 'Anuluj',
  'action.submit': 'Prześlij',
  'action.approve': 'Zatwierdź',
  'action.reject': 'Odrzuć',
  'action.download': 'Pobierz',
  'action.export': 'Eksportuj',
  'action.import': 'Importuj',
  'action.filter': 'Filtruj',
  'action.search': 'Szukaj',
  'action.refresh': 'Odśwież',
  'action.close': 'Zamknij',
  'action.back': 'Wstecz',
  'action.next': 'Dalej',
  'action.previous': 'Poprzedni',
  'action.viewAll': 'Pokaż wszystkie',
  'action.viewDetails': 'Pokaż szczegóły',
  'action.generate': 'Generuj',
  'action.signOut': 'Wyloguj się',
  'action.addNew': 'Dodaj nowy',
  'action.invite': 'Zaproś',

  // Statuses
  'status.draft': 'Wersja robocza',
  'status.submitted': 'Przesłano',
  'status.validated': 'Zwalidowano',
  'status.approved': 'Zatwierdzono',
  'status.rejected': 'Odrzucono',
  'status.pending': 'Oczekuje',
  'status.inProgress': 'W toku',
  'status.overdue': 'Zaległe',
  'status.compliant': 'Zgodne',
  'status.filed': 'Zgłoszono',
  'status.active': 'Aktywne',
  'status.inactive': 'Nieaktywne',
  'status.closed': 'Zamknięte',
  'status.fundraising': 'Pozyskiwanie środków',
  'status.investing': 'Inwestowanie',
  'status.harvesting': 'Zbieranie zysków',

  // Dashboard Labels
  'dashboard.portfolioOverview': 'Przegląd portfela',
  'dashboard.complianceStatus': 'Status zgodności',
  'dashboard.collectionProgress': 'Postęp zbierania',
  'dashboard.activityFeed': 'Aktywność',
  'dashboard.quickActions': 'Szybkie akcje',
  'dashboard.investmentPipeline': 'Pipeline inwestycyjny',
  'dashboard.totalFunds': 'Łącznie funduszy',
  'dashboard.aum': 'AUM',
  'dashboard.activeInvestments': 'Aktywne inwestycje',
  'dashboard.totalInvestees': 'Podmioty inwestycji',
  'dashboard.recentActivity': 'Ostatnia aktywność',

  // LP Dashboard Labels
  'lp.totalCommitted': 'Łącznie zobowiązań',
  'lp.capitalCalled': 'Wezwany kapitał',
  'lp.distributed': 'Rozdystrybuowano',
  'lp.netPosition': 'Pozycja netto',
  'lp.tvpi': 'TVPI',
  'lp.dpi': 'DPI',
  'lp.fundPerformance': 'Wyniki funduszy',
  'lp.esgSummary': 'ESG Podsumowanie',
  'lp.reportLibrary': 'Biblioteka raportów',
  'lp.taxonomyAligned': 'Zgodne z taksonomią',
  'lp.paiCoverage': 'PAI Pokrycie',
  'lp.portfolioPerformance': 'Wyniki portfela LP',

  // Fund Labels
  'fund.name': 'Nazwa funduszu',
  'fund.code': 'Kod funduszu',
  'fund.vintage': 'Rok założenia',
  'fund.size': 'Wielkość funduszu',
  'fund.targetSize': 'Docelowa wielkość',
  'fund.currency': 'Waluta',
  'fund.strategy': 'Strategia',
  'fund.status': 'Status',
  'fund.sfdrCategory': 'SFDR Kategoria',
  'fund.geographyFocus': 'Fokus geograficzny',
  'fund.sectorFocus': 'Fokus sektorowy',

  // Form Labels
  'form.name': 'Nazwa',
  'form.email': 'E-mail',
  'form.country': 'Kraj',
  'form.amount': 'Kwota',
  'form.currency': 'Waluta',
  'form.date': 'Data',
  'form.description': 'Opis',
  'form.notes': 'Notatki',
  'form.type': 'Typ',
  'form.period': 'Okres',
  'form.year': 'Rok',
  'form.required': 'Wymagane',
  'form.optional': 'Opcjonalne',

  // Table Headers
  'table.fund': 'Fundusz',
  'table.commitment': 'Zobowiązanie',
  'table.called': 'Wezwano',
  'table.distributed': 'Rozdystrybuowano',
  'table.tvpi': 'TVPI',
  'table.status': 'Status',
  'table.regulation': 'Regulacja',
  'table.filingType': 'Typ zgłoszenia',
  'table.reportingPeriod': 'Okres',
  'table.createdAt': 'Utworzono',
  'table.actions': 'Akcje',

  // Compliance
  'compliance.sfdr': 'SFDR',
  'compliance.taxonomy': 'Taksonomia UE',
  'compliance.pai': 'PAI',
  'compliance.dataCompleteness': 'Kompletność danych',
  'compliance.nextDeadline': 'Następny termin',

  // Reports
  'report.quarterly': 'Raport kwartalny',
  'report.annual': 'Raport roczny',
  'report.paiStatement': 'PAI Oświadczenie',
  'report.taxonomyReport': 'Raport taksonomii',
  'report.generatedAt': 'Wygenerowano',
  'report.version': 'Wersja',

  // General
  'general.loading': 'Ładowanie...',
  'general.noData': 'Brak dostępnych danych',
  'general.error': 'Wystąpił błąd',
  'general.retry': 'Spróbuj ponownie',
  'general.total': 'Łącznie',
  'general.page': 'Strona',
  'general.of': 'z',
  'general.showing': 'Wyświetlanie',
  'general.results': 'wyników',
  'general.language': 'Język',
  'general.english': 'Angielski',
  'general.french': 'Francuski',
};
